import { useState } from "react";
import { Carousel } from "react-responsive-carousel";
import Image from "next/image";
import "react-responsive-carousel/lib/styles/carousel.min.css";

import useViewport, { Viewport } from "../hooks/useViewport";

import Hero from "../components/hero";
import CarouselButtons from "../components/carousel-buttons/carousel-buttons";
import OurValues from "../components/our-values";

import styles from "./inclusion-and-diversity.module.scss";
import SeoMeta from "../components/seo-meta";
import { getSEOPageProps } from "../lib/seo";
import config from "../config";

const headerImages = {
  desktop: {
    src: "/images/inclusion.png",
    width: 1244,
    height: 679,
  },
  mobile: {
    src: "/images/inclusion.png",
    width: 375,
    height: 258,
  },
};

export default function InclusionAndDiversityPage({ seo }) {
  const device = useViewport();
  const [currentSlide, setCurrentSlide] = useState(0);

  const imageProps =
    device !== Viewport.PHONE ? headerImages.desktop : headerImages.mobile;

  return (
    <>
      <div className="container">
        <SeoMeta {...seo} />
        <Hero
          title="Inclusion & Diversity"
          description="East Room was founded in 2014 with the vision to develop an environment that enhances the personal and professional well-being of our members and extended community through intentional design, authentic programming, diverse community, and exceptional hospitality."
        />
      </div>

      <div className={styles.BeigeBackground}>
        <div className="container">
          <div className={styles.FullWidth}>
            <Image
              {...imageProps}
              className={styles.FullWidthImage}
              layout="responsive"
              objectFit="contain"
              alt="Inclusion and Diversity"
            />
          </div>
        </div>
      </div>

      <div className="container">
        <div className={styles.Columns}>
          <div className="block md:hidden">
            <div className={styles.Section}>
              <h2>Inclusive Environments &amp; Diverse Community</h2>
              <p>
                Our spaces and programming are designed around developing a
                diverse, connected and inclusive community. We act as a support
                system that is necessary for any individual or company to
                develop, establish or push your current boundaries.{" "}
              </p>
              <p>
                We believe strength comes from our diversity where our spaces
                are designed to accommodate introverts and extroverts, creatives
                and analytical types, and people from all different backgrounds.
                One by one, we introduce new members to existing members,
                creating opportunities to connect and create new ideas where
                perspectives can merge.
              </p>
            </div>
          </div>
          <div className={styles.LeftColumn}>
            <Image
              src="/images/jagged-lens-first-floor-lounge.jpg"
              width="578"
              height="905"
              layout="responsive"
              objectFit="cover"
              alt="First Floor Lounge"
            />
          </div>
          <div className={styles.RightColumn}>
            <div className={styles.Section}>
              <div className="hidden md:block">
                <h2>Inclusive Environments &amp; Diverse Community</h2>
                <p>
                  Our spaces and programming are designed around developing a
                  diverse, connected and inclusive community. We act as a
                  support system that is necessary for any individual or company
                  to develop, establish or push your current boundaries.{" "}
                </p>
                <p>
                  We believe strength comes from our diversity where our spaces
                  are designed to accommodate introverts and extroverts,
                  creatives and analytical types, and people from all different
                  backgrounds. One by one, we introduce new members to existing
                  members, creating opportunities to connect and create new
                  ideas where perspectives can merge.
                </p>
              </div>
            </div>

            <div className={styles.Section}>
              <h2>Culture &amp; Conduct</h2>
              <p>
                We are comitted to building and fostering an inclusive culture
                where all members, employees and guests are treated with respect
                and dignity. Our shared values, goals and practices are set to
                define a workspace where everyone can feel at home and
                represented.
              </p>
              <p>
                We value diversity and welcome a community of all backgrounds,
                genders, beliefs, sexual orientations, and all differences. We
                are a harassement-free workspace and encourage you to be
                yourself.
              </p>
            </div>

            <div className={styles.Section}>
              <h2>Community Engagement</h2>
              <p>
                As a community driven environment we believe in establishing a
                connecting point for the broader community. We seek to create a
                relationship and develop opportunities that positively impact
                the neighbourhoods we inhabit.
              </p>
              <p>
                Through local partnerships, hosting in-house Residencies with
                community initiatives, quarterly drives supporting various
                charities within our neighbourhood, and internal programming. We
                look to further the community within and outside of our
                workspace.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className={styles.Cards}>
          <div className={styles.Card}>
            <div className={styles.CardImage}>
              <Image
                src="/images/youth-basketball.jpg"
                width="395"
                height="395"
                layout="responsive"
                alt=""
              />
            </div>
            <div className={styles.CardTitle}>
              Lay-Up Youth Basketball Residence
            </div>
            <div className={styles.CardDescription}>
              Lay-Up Youth Basketball develops skills to succeed on and off the
              court. The Lay-Up Residence assembled 300 play-at-home kits for a
              virtual summer program.
            </div>
          </div>
          <div className={styles.Card}>
            <div className={styles.CardImage}>
              <Image
                src="/images/hand-up-toronto.jpg"
                width="395"
                height="395"
                layout="responsive"
                alt=""
              />
            </div>
            <div className={styles.CardTitle}>Hand Up Toronto Residence</div>
            <div className={styles.CardDescription}>
              Hand Up Toronto strives to create a society without hunger. The
              Hand Up (Feed the Six) Residence helped facilitate 1650 grocery
              care packages to help alleviate food insecurity in Toronto.
            </div>
          </div>
          <div className={styles.Card}>
            <div className={styles.CardImage}>
              <Image
                src="/images/lunch-hour-program.jpg"
                width="395"
                height="395"
                layout="responsive"
                alt=""
              />
            </div>
            <div className={styles.CardTitle}>Lunch Hour Program</div>
            <div className={styles.CardDescription}>
              Our monthly Lunch Hour Program fosters a relationship with small
              local businesses within our surrounding neighbourhood and
              community.
            </div>
          </div>
        </div>

        <div className={styles.MobileCarousel}>
          <Carousel
            showArrows={false}
            showStatus={false}
            infiniteLoop={false}
            showIndicators={false}
            autoPlay={false}
            selectedItem={currentSlide}
            swipeScrollTolerance={20}
            preventMovementUntilSwipeScrollTolerance
            onChange={(index) => setCurrentSlide(index)}
          >
            <div className={styles.Card}>
              <div className={styles.CardImage}>
                <Image
                  src="/images/youth-basketball.jpg"
                  width="395"
                  height="395"
                  layout="responsive"
                  alt=""
                />
              </div>
              <div className={styles.CardTitle}>
                Lay-Up Youth Basketball Residence
              </div>
              <div className={styles.CardDescription}>
                Lay-Up Youth Basketball develops skills to succeed on and off
                the court. The Lay-Up Residence assembled 300 play-at-home kits
                for a virtual summer program.
              </div>
            </div>
            <div className={styles.Card}>
              <div className={styles.CardImage}>
                <Image
                  src="/images/hand-up-toronto.jpg"
                  width="395"
                  height="395"
                  layout="responsive"
                  alt=""
                />
              </div>
              <div className={styles.CardTitle}>Hand Up Toronto Residence</div>
              <div className={styles.CardDescription}>
                Hand Up Toronto strives to create a society without hunger. The
                Hand Up (Feed the Six) Residence helped facilitate 1650 grocery
                care packages to help alleviate food insecurity in Toronto.
              </div>
            </div>
            <div className={styles.Card}>
              <div className={styles.CardImage}>
                <Image
                  src="/images/lunch-hour-program.jpg"
                  width="395"
                  height="395"
                  layout="responsive"
                  alt=""
                />
              </div>
              <div className={styles.CardTitle}>Lunch Hour Program</div>
              <div className={styles.CardDescription}>
                Our monthly Lunch Hour Program fosters a relationship with small
                local businesses within our surrounding neighbourhood and
                community.
              </div>
            </div>
          </Carousel>
          <CarouselButtons
            slides={[1, 2, 3].map((post, index) => ({
              active: index === currentSlide,
            }))}
            onChange={(index) => setCurrentSlide(index)}
          />
        </div>
      </div>

      <div className="container">
        <OurValues />
      </div>
    </>
  );
}

export async function getStaticProps() {
  return {
    props: {
      seo: await getSEOPageProps({ resolvedUrl: "/inclusion-and-diversity" }),
    },
    revalidate: config.ISR_TIMING,
  };
}
